import { useState } from "react";
import { Download, Send, Upload, FileCheck2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DOCUMENT_LIBRARY, UPLOAD_SIGNATURE_DIALOG, MANUAL_CONFIRMATION } from "@/constants/testIds";
import { ManualConfirmDialog } from "@/components/ManualConfirmDialog";
import { toast } from "sonner";

const STATUS_META = {
  generated: { label: "Generado", className: "bg-charcoal/10 text-charcoal/60" },
  enviado_para_revision: { label: "Enviado a revisión", className: "bg-petroleum/10 text-petroleum" },
  pendiente_firma_fisica: { label: "Pendiente de firma física", className: "bg-amber-50 text-amber-700" },
  firmado_fisico_respaldo_digital: { label: "Firmado físico (respaldo digital)", className: "bg-emerald-50 text-emerald-700" },
  confirmado_manual_admin: { label: "Confirmado manualmente por RH", className: "bg-emerald-50 text-emerald-700" },
  reemplazado: { label: "Reemplazado (versión anterior)", className: "bg-charcoal/5 text-charcoal/40" },
};

export function SignableDocumentRow({ doc, onDownloadPdf, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const meta = STATUS_META[doc.status] || STATUS_META.generated;

  const sendForReview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/signable-documents/${doc.id}/send-for-review`);
      toast.success(data.message);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadSignature = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/signable-documents/${doc.id}/upload-physical-signature`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.message);
      setUploadOpen(false);
      setFile(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const viewSignedFile = async () => {
    try {
      const { data } = await api.get(`/signable-documents/${doc.id}/signed-file`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
    } catch {
      toast.error("No se pudo abrir el documento firmado.");
    }
  };

  const markSignedManual = async (note) => {
    try {
      const { data } = await api.post(`/signable-documents/${doc.id}/mark-signed-manual`, { note });
      toast.success(data.message);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const NON_TERMINAL_STATUSES = ["generated", "enviado_para_revision", "pendiente_firma_fisica"];

  return (
    <div data-testid={DOCUMENT_LIBRARY.employeeDocRow} className="bg-card border border-petroleum/10 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium text-charcoal">{doc.title}</p>
          <p className="text-xs text-charcoal/50">Generado por {doc.generated_by} · hash {doc.hash.slice(0, 12)}…</p>
          <span data-testid={DOCUMENT_LIBRARY.employeeDocStatusBadge} className={`inline-flex mt-2 text-xs font-medium px-2.5 py-1 rounded-full ${meta.className}`}>
            {meta.label}
          </span>
          {doc.was_corrected && <span className="inline-flex mt-2 ml-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-charcoal/10 text-charcoal/60">Corregido</span>}
          {doc.status === "confirmado_manual_admin" && doc.manual_confirmation && (
            <p data-testid={MANUAL_CONFIRMATION.signableBadge} className="text-xs text-charcoal/50 mt-2">
              Confirmado manualmente por {doc.manual_confirmation.by}{doc.manual_confirmation.note ? `: "${doc.manual_confirmation.note}"` : ""}
            </p>
          )}
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap justify-end">
          <Button data-testid={DOCUMENT_LIBRARY.employeeDocPdfButton} size="sm" variant="outline" onClick={() => onDownloadPdf(doc)} className="rounded-full border-petroleum/30 text-petroleum">
            <Download className="w-4 h-4 mr-1" /> PDF
          </Button>
          {doc.status === "generated" && (
            <Button data-testid={DOCUMENT_LIBRARY.sendForReviewButton} size="sm" onClick={sendForReview} disabled={busy} className="rounded-full bg-terracotta hover:bg-terracotta/90">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Enviar a revisión
            </Button>
          )}
          {(doc.status === "pendiente_firma_fisica" || doc.status === "firmado_fisico_respaldo_digital") && (
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button data-testid={DOCUMENT_LIBRARY.uploadSignatureButton} size="sm" variant={doc.status === "firmado_fisico_respaldo_digital" ? "outline" : "default"} className={doc.status === "firmado_fisico_respaldo_digital" ? "rounded-full border-petroleum/30 text-petroleum" : "rounded-full bg-petroleum hover:bg-petroleum/90"}>
                  <Upload className="w-4 h-4 mr-1" /> {doc.status === "firmado_fisico_respaldo_digital" ? "Corregir archivo" : "Subir firma física"}
                </Button>
              </DialogTrigger>
              <DialogContent data-testid={UPLOAD_SIGNATURE_DIALOG.dialog} className="bg-bone">
                <DialogHeader>
                  <DialogTitle>{doc.status === "firmado_fisico_respaldo_digital" ? "Corregir documento firmado" : "Subir documento firmado en físico"}</DialogTitle>
                  <DialogDescription>
                    {doc.status === "firmado_fisico_respaldo_digital"
                      ? "Si la foto o escaneo anterior salió borrosa o incorrecta, sube una nueva. El archivo y el registro anteriores no se borran: quedan en el historial inmutable, y este nuevo archivo pasa a ser el vigente para consulta."
                      : "Sube la foto o escaneo del documento ya firmado por ambas partes (PDF, JPG o PNG, máx. 10 MB)."}
                  </DialogDescription>
                </DialogHeader>
                <input
                  data-testid={UPLOAD_SIGNATURE_DIALOG.fileInput}
                  type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="text-sm"
                />
                <Button data-testid={UPLOAD_SIGNATURE_DIALOG.submitButton} onClick={uploadSignature} disabled={!file || busy} className="w-full bg-petroleum rounded-full">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Confirmar y guardar
                </Button>
              </DialogContent>
            </Dialog>
          )}
          {(doc.status === "firmado_fisico_respaldo_digital" || doc.status === "reemplazado") && (
            <Button data-testid={DOCUMENT_LIBRARY.viewSignedFileButton} size="sm" variant="outline" onClick={viewSignedFile} className="rounded-full border-emerald-600/30 text-emerald-700">
              <FileCheck2 className="w-4 h-4 mr-1" /> Ver firma subida
            </Button>
          )}
          {NON_TERMINAL_STATUSES.includes(doc.status) && (
            <ManualConfirmDialog
              triggerTestId={`${MANUAL_CONFIRMATION.signableButton}-${doc.id}`}
              triggerLabel="Marcar como firmado manualmente"
              title="Marcar como firmado/confirmado manualmente"
              description="Para empleados sin acceso al portal (ej. firmó en papel y el documento está en su expediente físico). No se adjunta foto ni archivo — solo queda registrado quién lo marcó, cuándo, y una nota opcional."
              onConfirm={markSignedManual}
            />
          )}
        </div>
      </div>
    </div>
  );
}
